import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Gauge, Fuel, Settings2, Zap, Phone, Globe, Building2, ChevronLeft, ChevronRight, MessageCircle, CalendarDays, Clock, Tag, Store, Check, Loader2 } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import MakeOfferModal from '../components/MakeOfferModal';
import { formatPrice, formatMileage, formatPowerFull } from '../utils/formatters';
import { getTransmissionLabel, getFuelTypeLabel, getBodyTypeLabel, getDriveTypeLabel, getInteriorTypeLabel, getPaintTypeLabel, getEuroStandardLabel, DAYS_FR } from '../constants/vehicleFields';

const MarketplaceListingDetail = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentImage, setCurrentImage] = useState(0);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerSent, setOfferSent] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [appointmentBooked, setAppointmentBooked] = useState(false);
  const [bookingError, setBookingError] = useState('');

  useEffect(() => {
    const loadListing = async () => {
      try {
        const response = await api.get(`/marketplace/listings/${id}`);
        setListing(response.data);
        // Load available slots for the dealer
        const dealerId = response.data?.dealer?.id;
        if (dealerId && !response.data.is_own) {
          setSlotsLoading(true);
          try {
            const slotsRes = await api.get(`/marketplace/appointments/slots/${dealerId}`);
            setSlots(slotsRes.data.slots || []);
          } catch (e) {
            // Silently fail - slots are optional
          } finally {
            setSlotsLoading(false);
          }
        }
      } catch (err) {
        setError('Annonce non trouvée');
      } finally {
        setLoading(false);
      }
    };
    loadListing();
  }, [id]);

  const bookAppointment = async (date, time) => {
    setBookingSlot(`${date}-${time}`);
    setBookingError('');
    try {
      await api.post('/marketplace/appointments', {
        listing_id: id,
        date,
        time,
      });
      setAppointmentBooked(true);
      // Mark the slot as booked locally
      setSlots(prev => prev.map(s =>
        s.date === date && s.time === time ? { ...s, booked: true } : s
      ));
    } catch (err) {
      setBookingError(err.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setBookingSlot(null);
    }
  };

  // Group slots by date for display
  const slotsByDate = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      const d = new Date(slot.date + 'T00:00:00');
      acc[slot.date] = {
        date: slot.date,
        dayLabel: DAYS_FR[slot.day_key] || '',
        dateLabel: d.toLocaleDateString('fr-BE', { day: 'numeric', month: 'long' }),
        slots: [],
      };
    }
    acc[slot.date].slots.push(slot);
    return acc;
  }, {});
  const dateGroups = Object.values(slotsByDate).slice(0, 7);

  if (loading) return <div className="py-12"><LoadingSpinner size="lg" /></div>;
  if (error) return (
    <div className="text-center py-16">
      <p className="text-red-400 mb-4">{error}</p>
      <Link to="/marketplace" className="text-carmin hover:text-carmin-light">Retour à la marketplace</Link>
    </div>
  );
  if (!listing) return null;

  const vehicle = listing.vehicle || {};
  const dealer = listing.dealer || {};
  const images = vehicle.images || [];
  const whatsappUrl = dealer.phone
    ? `https://wa.me/${dealer.phone.replace(/[^0-9+]/g, '')}?text=${encodeURIComponent(`Bonjour, je suis intéressé par votre ${vehicle.make} ${vehicle.model} ${vehicle.year || ''} publiée sur la Marketplace Carmin.\n\nEst-elle toujours disponible ?`)}`
    : null;

  return (
    <div>
      {/* Back link */}
      <Link to="/marketplace" className="inline-flex items-center text-muted hover:text-label mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" /> Retour à la marketplace
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Images + Vehicle info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image gallery */}
          <div className="bg-dark-card rounded-xl overflow-hidden">
            <div className="aspect-video bg-dark-hover relative">
              {images.length > 0 ? (
                <img
                  src={images[currentImage]}
                  alt={`${vehicle.make} ${vehicle.model}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted">Pas d'image</div>
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImage(i => (i - 1 + images.length) % images.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentImage(i => (i + 1) % images.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full">
                    {currentImage + 1} / {images.length}
                  </div>
                </>
              )}
            </div>
            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImage(i)}
                    className={`w-16 h-12 flex-shrink-0 rounded overflow-hidden border-2 ${
                      i === currentImage ? 'border-carmin' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Vehicle specs */}
          <div className="bg-dark-card rounded-xl p-6">
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-primary">
                {vehicle.make} {vehicle.model}
              </h1>
              {vehicle.version && <p className="text-muted">{vehicle.version}</p>}
            </div>

            {/* Price TVAC */}
            <div className="mb-6">
              <p className="text-3xl font-bold text-carmin">
                {listing.pro_price ? formatPrice(listing.pro_price) : 'Prix sur demande'}
                {listing.pro_price && <span className="text-base font-normal text-muted ml-2">TVAC</span>}
              </p>
            </div>

            {/* Main specs - icon grid like mobile.de */}
            <div className="grid grid-cols-2 gap-4">
              {vehicle.mileage != null && (
                <div className="flex items-center gap-3">
                  <Gauge className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Kilométrage</p>
                    <p className="text-sm font-semibold text-primary">{formatMileage(vehicle.mileage)}</p>
                  </div>
                </div>
              )}
              {vehicle.power_kw && (
                <div className="flex items-center gap-3">
                  <Zap className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Puissance</p>
                    <p className="text-sm font-semibold text-primary">{formatPowerFull(vehicle.power_kw)}</p>
                  </div>
                </div>
              )}
              {vehicle.fuel_type && (
                <div className="flex items-center gap-3">
                  <Fuel className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Carburant</p>
                    <p className="text-sm font-semibold text-primary">{getFuelTypeLabel(vehicle.fuel_type)}</p>
                  </div>
                </div>
              )}
              {vehicle.transmission && (
                <div className="flex items-center gap-3">
                  <Settings2 className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Transmission</p>
                    <p className="text-sm font-semibold text-primary">{getTransmissionLabel(vehicle.transmission, 'full')}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Calendar className="h-7 w-7 text-carmin flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted">Année</p>
                  <p className="text-sm font-semibold text-primary">{vehicle.year}</p>
                </div>
              </div>
            </div>

            {/* Technical data - alternating rows like mobile.de */}
            {(() => {
              const rows = [
                vehicle.body_type && ['Carrosserie', getBodyTypeLabel(vehicle.body_type)],
                vehicle.color && ['Couleur', vehicle.color],
                vehicle.paint_type && ['Type de peinture', getPaintTypeLabel(vehicle.paint_type)],
                vehicle.doors && ['Portes', vehicle.doors],
                vehicle.seats && ['Sièges', vehicle.seats],
                vehicle.engine_displacement_cc && ['Cylindrée', `${vehicle.engine_displacement_cc} cm³`],
                vehicle.cylinders && ['Cylindres', vehicle.cylinders],
                vehicle.gears && ['Vitesses', vehicle.gears],
                vehicle.drive_type && ['Transmission', getDriveTypeLabel(vehicle.drive_type)],
                vehicle.weight && ['Poids', `${vehicle.weight} kg`],
                vehicle.first_registration && ['1ère immatriculation', vehicle.first_registration],
                vehicle.previous_owners != null && ['Propriétaires précédents', vehicle.previous_owners],
              ].filter(Boolean);
              if (rows.length === 0) return null;
              return (
                <div className="mt-6">
                  <h3 className="text-lg font-bold text-primary mb-3">Données techniques</h3>
                  <div className="rounded-lg overflow-hidden">
                    {rows.map(([label, value], i) => (
                      <div key={label} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-dark-hover' : ''}`}>
                        <span className="text-sm text-secondary font-medium">{label}</span>
                        <span className="text-sm text-primary">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Interior */}
            {(vehicle.interior_type || vehicle.interior_color) && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-primary mb-3">Intérieur</h3>
                <div className="rounded-lg overflow-hidden">
                  {vehicle.interior_type && (
                    <div className="flex items-center justify-between px-4 py-2.5 bg-dark-hover">
                      <span className="text-sm text-secondary font-medium">Sellerie</span>
                      <span className="text-sm text-primary">{getInteriorTypeLabel(vehicle.interior_type)}</span>
                    </div>
                  )}
                  {vehicle.interior_color && (
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-sm text-secondary font-medium">Couleur intérieure</span>
                      <span className="text-sm text-primary">{vehicle.interior_color}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Consumption & Environment */}
            {(vehicle.fuel_consumption || vehicle.co2_emissions || vehicle.euro_standard || vehicle.has_particle_filter != null) && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-primary mb-3">Consommation & Environnement</h3>
                <div className="rounded-lg overflow-hidden">
                  {(() => {
                    const ecoRows = [
                      vehicle.fuel_consumption && ['Consommation', vehicle.fuel_consumption],
                      vehicle.co2_emissions && ['Émissions CO₂', `${vehicle.co2_emissions} g/km`],
                      vehicle.euro_standard && ['Norme Euro', getEuroStandardLabel(vehicle.euro_standard)],
                      vehicle.has_particle_filter != null && ['Filtre à particules', vehicle.has_particle_filter ? 'Oui' : 'Non'],
                    ].filter(Boolean);
                    return ecoRows.map(([label, value], i) => (
                      <div key={label} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-dark-hover' : ''}`}>
                        <span className="text-sm text-secondary font-medium">{label}</span>
                        <span className="text-sm text-primary">{value}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Condition & History */}
            {(vehicle.had_accident != null || vehicle.has_full_service_history != null || vehicle.non_smoking != null || vehicle.warranty || vehicle.tax_deductible != null) && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-primary mb-3">État & Historique</h3>
                <div className="rounded-lg overflow-hidden">
                  {(() => {
                    const condRows = [
                      vehicle.had_accident != null && ['Accident', vehicle.had_accident ? 'Oui' : 'Sans accident'],
                      vehicle.has_full_service_history != null && ['Historique d\'entretien', vehicle.has_full_service_history ? 'Complet' : 'Partiel'],
                      vehicle.non_smoking != null && ['Non fumeur', vehicle.non_smoking ? 'Oui' : 'Non'],
                      vehicle.warranty && ['Garantie', vehicle.warranty],
                      vehicle.tax_deductible != null && ['TVA déductible', vehicle.tax_deductible ? 'Oui' : 'Non'],
                    ].filter(Boolean);
                    return condRows.map(([label, value], i) => (
                      <div key={label} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-dark-hover' : ''}`}>
                        <span className="text-sm text-secondary font-medium">{label}</span>
                        <span className="text-sm text-primary">{value}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Equipment checklist */}
            {vehicle.equipment && vehicle.equipment.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-bold text-primary mb-3">Équipements</h3>
                <div className="rounded-lg overflow-hidden">
                  {vehicle.equipment.map((item, i) => (
                    <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-dark-hover' : ''}`}>
                      <span className="text-sm text-label">{item}</span>
                      <Check className="h-4 w-4 text-muted" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            {listing.description && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-label mb-2">Description</h3>
                <p className="text-sm text-secondary whitespace-pre-line">{listing.description}</p>
              </div>
            )}

            {/* Notes */}
            {listing.notes && (
              <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-sm font-medium text-amber-400 mb-1">Notes du vendeur</p>
                <p className="text-sm text-amber-400">{listing.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar: Offer + Dealer info + Contact + Calendar */}
        <div className="space-y-4">
          {/* Make an offer - primary CTA */}
          {!listing.is_own && (
            <div className="bg-dark-card rounded-xl p-5">
              {offerSent ? (
                <div className="text-center py-2">
                  <div className="w-12 h-12 bg-carmin-50 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Tag className="h-6 w-6 text-carmin" />
                  </div>
                  <p className="font-semibold text-carmin">Offre envoyée !</p>
                  <p className="text-xs text-muted mt-1">Le vendeur recevra votre offre et pourra y répondre.</p>
                  <Link to="/marketplace/offers" className="text-sm text-carmin hover:text-carmin-light mt-2 inline-block">
                    Voir mes offres
                  </Link>
                </div>
              ) : (
                <button
                  onClick={() => setShowOfferModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-3.5 bg-carmin text-white rounded-xl font-semibold hover:bg-carmin-light transition-colors text-base"
                >
                  <Tag className="h-5 w-5" />
                  Faire une offre
                </button>
              )}
            </div>
          )}

          {/* Dealer card */}
          <div className="bg-dark-card rounded-xl p-5">
            <h3 className="text-sm font-semibold text-primary mb-3">Vendeur professionnel</h3>

            <div className="flex items-center gap-3 mb-4">
              {dealer.logo_url ? (
                <img src={dealer.logo_url} alt="" className="w-11 h-11 rounded-full object-cover" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-carmin-50 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-carmin" />
                </div>
              )}
              <div>
                <p className="font-semibold text-primary text-sm">{dealer.display_name}</p>
                {dealer.company && dealer.company !== dealer.display_name && (
                  <p className="text-xs text-muted">{dealer.company}</p>
                )}
              </div>
            </div>

            {/* Contact buttons */}
            <div className="space-y-2">
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-carmin text-white rounded-xl hover:bg-carmin-light transition-colors font-medium text-sm"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
              )}
              {dealer.phone && (
                <a
                  href={`tel:${dealer.phone}`}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-dark-border rounded-xl hover:bg-dark-hover text-label transition-colors text-sm"
                >
                  <Phone className="h-4 w-4" />
                  {dealer.phone}
                </a>
              )}
              {dealer.website && (
                <a
                  href={dealer.website.startsWith('http') ? dealer.website : `https://${dealer.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-dark-border rounded-xl hover:bg-dark-hover text-label transition-colors text-sm"
                >
                  <Globe className="h-4 w-4" />
                  Site web
                </a>
              )}

              {/* See dealer's other listings */}
              {dealer.listing_count > 1 && (
                <Link
                  to={`/marketplace?dealer=${dealer.id}`}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-carmin-dark/50 rounded-lg hover:bg-carmin-50 text-carmin transition-colors font-medium"
                >
                  <Store className="h-5 w-5" />
                  Voir ses {dealer.listing_count} annonces
                </Link>
              )}
            </div>
          </div>

          {/* Visit booking - 30 min slots */}
          {!listing.is_own && dateGroups.length > 0 && (
            <div className="bg-dark-card rounded-xl p-5">
              <h3 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-carmin" />
                Prendre rendez-vous
              </h3>

              {appointmentBooked ? (
                <div className="text-center py-3">
                  <div className="w-10 h-10 bg-carmin-50 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Check className="h-5 w-5 text-carmin" />
                  </div>
                  <p className="font-semibold text-carmin text-sm">Demande envoyée !</p>
                  <p className="text-xs text-muted mt-1">Le vendeur sera notifié et pourra confirmer votre visite.</p>
                  <Link to="/marketplace/appointments" className="text-sm text-carmin hover:text-carmin-light mt-2 inline-block">
                    Voir mes rendez-vous
                  </Link>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted mb-3">Choisissez un créneau de 30 min</p>

                  {bookingError && (
                    <div className="text-xs text-red-400 bg-red-500/10 rounded-lg p-2 mb-3">{bookingError}</div>
                  )}

                  {/* Date tabs */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {dateGroups.map((group, i) => (
                      <button
                        key={group.date}
                        onClick={() => setSelectedDate(selectedDate === group.date ? null : group.date)}
                        className={`px-2 py-2.5 rounded-lg border text-center transition-all ${
                          selectedDate === group.date
                            ? 'border-carmin bg-carmin-50 text-carmin'
                            : 'border-dark-border text-secondary hover:border-carmin-dark'
                        }`}
                      >
                        <p className="text-xs font-semibold">{group.dayLabel}</p>
                        <p className="text-[11px] text-muted mt-0.5">{group.dateLabel}</p>
                      </button>
                    ))}
                  </div>

                  {/* Slot chips for selected date */}
                  {selectedDate && slotsByDate[selectedDate] && (
                    <div className="flex flex-wrap gap-2">
                      {slotsByDate[selectedDate].slots.map((slot) => {
                        const isBooking = bookingSlot === `${slot.date}-${slot.time}`;
                        return (
                          <button
                            key={`${slot.date}-${slot.time}`}
                            disabled={slot.booked || isBooking}
                            onClick={() => bookAppointment(slot.date, slot.time)}
                            className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              slot.booked
                                ? 'bg-dark-hover text-muted cursor-not-allowed line-through'
                                : isBooking
                                  ? 'bg-carmin-50 text-carmin border border-carmin-dark/50'
                                  : 'bg-dark-surface border border-dark-border text-label hover:border-carmin hover:bg-carmin-50 hover:text-carmin'
                            }`}
                          >
                            {isBooking ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Clock className="h-3.5 w-3.5" />
                            )}
                            {slot.time}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {!selectedDate && (
                    <p className="text-xs text-muted text-center py-2">Sélectionnez une date ci-dessus</p>
                  )}
                </>
              )}
            </div>
          )}
          {slotsLoading && !listing.is_own && (
            <div className="bg-dark-card rounded-xl p-5 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted mx-auto" />
            </div>
          )}

          {/* Listing meta */}
          <div className="bg-dark-hover rounded-xl p-4 text-xs text-muted">
            <p>Publiée le {new Date(listing.created_at).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
      </div>

      {/* Offer modal */}
      {showOfferModal && (
        <MakeOfferModal
          listing={listing}
          onClose={() => setShowOfferModal(false)}
          onOfferSent={() => setOfferSent(true)}
        />
      )}
    </div>
  );
};

export default MarketplaceListingDetail;
